'use client';

import { useState } from 'react';
import { Chip, Tooltip } from '@mui/material';
import BulkOrderPreviewDialog from './BulkOrderPreviewDialog';

/**
 * Reusable Bulk Badge Component
 * Shows a clickable chip indicating a parcel is part of a bulk order.
 * Opens a preview dialog on click.
 *
 * @param {string|number} bulkOrderId - The bulk order ID
 * @param {string} bulkOrderReference - The bulk order reference (e.g., "BULK-123")
 * @param {number} itemCount - Number of items in the bulk order
 * @param {boolean} compact - Use compact styling (smaller chip)
 */
export default function BulkBadge({
  bulkOrderId,
  bulkOrderReference,
  itemCount = 0,
  compact = false,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!bulkOrderId || !bulkOrderReference) return null;

  const tooltip = `${bulkOrderReference} • ${itemCount} item${itemCount === 1 ? '' : 's'} • Click to view`;

  return (
    <>
      <Tooltip title={tooltip}>
        <Chip
          label={compact ? 'Bulk' : `Bulk ${bulkOrderReference}`}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setDialogOpen(true);
          }}
          sx={{
            fontWeight: 700,
            fontSize: compact ? '0.6rem' : '0.7rem',
            bgcolor: 'transparent',
            color: '#d97706',
            border: '1.5px solid #d97706',
            cursor: 'pointer',
            transition: 'all 200ms',
            textDecoration: 'none',
            '&:hover': {
              bgcolor: '#fef3c7',
              textDecoration: 'underline',
              transform: 'scale(1.05)',
            },
          }}
        />
      </Tooltip>

      <BulkOrderPreviewDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        bulkOrderId={bulkOrderId}
      />
    </>
  );
}
